import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Col,
  Dropdown,
  Form,
  OverlayTrigger,
  Placeholder,
  ProgressBar,
  Row,
  Spinner,
  Toast,
  ToastContainer,
  Tooltip,
} from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import StarsRating from "react-star-rate";

import DstButton from "../../components/DstButton/DstButton";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import ApiService from "../../services/ApiService";
import BroadcastService from "../../services/BroadcastService";
import { getExamStatusIcon, getStatusColor } from "../../services/ExamStatus";
import { PermissionComponent } from "../../services/PermissionGuard";
import shortcutService from "../../services/ShortcutService";
import { formatDateHumanly, formatSecondsMinutely } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate, truncateString } from "../../services/UtilityService";
import {
  downloadEvaluationAttemptNotebook,
  downloadEvaluationFileFromRender,
  sendCorrection,
  startCorrection,
} from "./Correction.function";
import { checkAllCorrected } from "./Correction.function";
import "./Correction.scss";
import ExamStatusDropdown from "./components/ExamStatusDropdown";
import HtmlEvaluationRender from "./components/HtmlEvaluationRender";

const Correction = () => {
  const { language, user_id } = useSession();
  const { evaluation_attempts_id } = useParams();
  const navigate = useNavigate();

  const [showOverlay, setShowOverlay] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [loading, setLoading] = useState({
    infos: true,
    render: true,
    submit: false,
    downloadNotebook: false,
  });
  const [evaluationInfos, setEvaluationInfos] = useState<any>({});
  const [evaluationRender, setEvaluationRender] = useState<any>(null);
  const [editorContent, setEditorContent] = useState("");
  const [isCorrectionStarted, setIsCorrectionStarted] = useState(false);
  const [examStatus, setExamStatus] = useState("");
  const [isAllCorrected, setIsAllCorrected] = useState(false);
  const [hasAlreadyCorrector, setHasAlreadyCorrector] = useState(false);
  const [isModifiedComment, setIsModifiedComment] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctQuestions, setCorrectQuestions] = useState(0);
  const [answersCorrection, setAnswersCorrection] = useState<Array<any>>([]);
  const [connectedUsers, setConnectedUsers] = useState<Array<any>>([]);
  const [correctorName, setCorrectorName] = useState("");
  const [showFullComment, setShowFullComment] = useState(false);
  const [score, setScore] = useState<number | undefined>(undefined);
  const [score_base] = useState<number>(20);
  const [showAiDetailModal, setshowAiDetailModal] = useState(false);
  const [notebookTimeoutError, setNotebookTimeoutError] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showCopyEvalToast, setShowCopyEvalToast] = useState(false);

  useEffect(() => {
    const socket = ApiService.createSocket("notifications");
    socket.emit("correction-room", { evaluation_attempts_id, user_id, action: true });
    socket.on("user-updated", (data: any) => {
      setConnectedUsers(data.user);
    });

    socket.on("correction-started", (data: any) => {
      if (data.evaluation_attempt_id === Number(evaluation_attempts_id)) {
        if (data.corrector_id !== Number(user_id)) {
          setHasAlreadyCorrector(true);
          setCorrectorName(data.corrector_name);
        }
      }
    });

    socket.on("error", (data: any) => {
      console.error("An error occurred:", data.message);
    });

    return () => {
      socket.emit("correction-room", { evaluation_attempts_id, user_id, action: false });
      socket.disconnect();
    };
  }, [evaluation_attempts_id, user_id]);

  const _copyEvalToClipboard = useCallback(() => {
    if (!evaluationInfos.user?.name || !evaluationInfos.evaluation?.name) return;
    navigator.clipboard
      .writeText(
        `${evaluationInfos.user?.name} | ${evaluationInfos.evaluation?.name} | ${window.location.href} | ${
          evaluationInfos.dt_ended ? formatDateHumanly(language, evaluationInfos.dt_ended, false) : "N/A"
        }`
      )
      .then(() => setShowCopyEvalToast(true))
      .catch((err) => console.error("Failed to copy evaluation info:", err));
  }, [evaluationInfos.user?.name, evaluationInfos.evaluation?.name, evaluationInfos.dt_ended, language]);

  useEffect(() => {
    const listen_destructor = shortcutService.listen("c", _copyEvalToClipboard, false, true);
    return () => {
      listen_destructor();
    };
  }, [_copyEvalToClipboard]);

  useEffect(() => {
    const bc = BroadcastService.open(`correction-${evaluation_attempts_id}`);

    bc.addEventListener("message", (event: MessageEvent) => {
      if (event.data?.type === "CORRECTION_UPDATED") {
        ApiService.get(`evaluations/attempts/${evaluation_attempts_id}/details`)
          .then((response) => setEvaluationInfos(response.data.evaluation_attempt_details))
          .catch((error) => console.error("Broadcast error correction:", error));
      }
    });

    return () => bc.close();
  }, [evaluation_attempts_id]);

  const handleShowOverlay = () => {
    setShowOverlay(true);
    setIsCorrectionStarted(true);
  };

  const normalizeContent = (content: any) => {
    const div = document.createElement("div");
    div.innerHTML = content;
    return div.innerText.trim().replace(/\s+/g, " ");
  };

  const _copyCurlCommand = () => {
    const apiUrl = `${process.env.REACT_APP_API_URL}/evaluations/attempts/${evaluation_attempts_id}/content`;
    const access_token = localStorage.getItem("nexus-access");
    const filename = evaluationRender.evaluation_upload_infos.filename;
    const curlCommand = `curl -H "Authorization: Bearer ${access_token}" ${apiUrl} -o ${filename}`;

    navigator.clipboard
      .writeText(curlCommand)
      .then(() => setShowCopyToast(true))
      .catch((err) => console.error("Failed to copy link:", err));
  };

  const _openExternalWithEmail = () => {
    navigator.clipboard
      .writeText(evaluationInfos?.user?.id)
      .then(() => window.open("https://extension-admin.mereos.eu/sessions?duplicates=false", "_blank"))
      .catch((err) => console.error("Failed to copy link:", err));
  };

  useEffect(() => {
    setLoading((loading) => ({ ...loading, infos: true }));
    ApiService.get(`evaluations/attempts/${evaluation_attempts_id}/details`)
      .then((resp) => setEvaluationInfos(resp.data.evaluation_attempt_details))
      .catch((err) => console.error(err))
      .finally(() => setLoading((loading) => ({ ...loading, infos: false })));
  }, [evaluation_attempts_id]);

  useEffect(() => {
    if (!evaluationInfos.evaluation?.type) return;

    setLoading((loading) => ({ ...loading, render: true }));
    ApiService.get(`evaluations/attempts/${evaluation_attempts_id}/render`)
      .then((resp) => setEvaluationRender(resp.data))
      .catch((err) => {
        console.error(err);
        if (
          evaluationInfos.evaluation?.type === "notebook" &&
          err?.data?.error?.type === "InternalServerError" &&
          err?.data?.error?.message?.includes("takes too long to render")
        ) {
          setNotebookTimeoutError(true);
        }
      })
      .finally(() => setLoading((loading) => ({ ...loading, render: false })));
  }, [evaluation_attempts_id, evaluationInfos.evaluation?.type]);

  useEffect(() => {
    checkAllCorrected(evaluationRender, setIsAllCorrected);
  }, [evaluationRender]);

  useEffect(() => {
    if (evaluationInfos.status === "being_corrected") {
      if (evaluationInfos.corrector.id === user_id) {
        setShowOverlay(true);
        setIsCorrectionStarted(true);
        setHasAlreadyCorrector(false);
      } else {
        setHasAlreadyCorrector(true);
        setCorrectorName(evaluationInfos.corrector.name);
      }
    }
  }, [user_id, evaluationInfos.corrector, evaluationInfos.status]);

  useEffect(() => {
    if (["pre_corrected", "being_corrected"].includes(evaluationInfos.status)) {
      setExamStatus(evaluationInfos.ai_prevision_status);
      setEditorContent(evaluationInfos.ai_prevision_comment);
      setScore(parseFloat(evaluationInfos.ai_prevision_score.split("/")[0]));
    }
  }, [evaluationInfos]);

  useEffect(() => {
    setIsModifiedComment(normalizeContent(evaluationInfos.ai_prevision_comment) !== normalizeContent(editorContent));
  }, [editorContent, evaluationInfos.ai_prevision_comment]);

  useEffect(() => {
    if (isAllCorrected) {
      if (correctQuestions / totalQuestions >= evaluationInfos.evaluation?.success_ratio) {
        setExamStatus("success");
      } else if (evaluationInfos.number_of_attempts < 3) {
        setExamStatus("repass");
      } else {
        setExamStatus("failed");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctQuestions, totalQuestions, isAllCorrected]);

  useEffect(() => {
    if (!loading.infos && evaluationInfos?.evaluation?.type === "html" && isCorrectionStarted) {
      if (evaluationRender?.some((item: any) => item.is_correct === null && !item.isEditable)) {
        const updatedEvaluationRender = evaluationRender.map((item: any) => {
          if (item.is_correct === null) {
            return { ...item, isEditable: true };
          }
          return item;
        });

        setEvaluationRender(updatedEvaluationRender);
      }
    }
  }, [loading.infos, evaluationInfos, isCorrectionStarted, evaluationRender]);

  const handleStatusChange = (newStatus: boolean, index: number) => {
    setEvaluationRender((prevEvaluationRender: any) => {
      const updatedItems = [...prevEvaluationRender];
      updatedItems[index] = { ...updatedItems[index], is_correct: newStatus };
      setAnswersCorrection((answersCorrection) => ({
        ...answersCorrection,
        [updatedItems[index].timestamp]: newStatus,
      }));
      return updatedItems;
    });
  };

  const _convertToBasicalScore = (score: string) => {
    const [obtained, total] = score.split("/").map(Number);
    if (isNaN(obtained) || isNaN(total) || total === 0) return "N/A";
    return ((obtained / total) * 20).toFixed(1).replace(".0", "");
  };

  const _cleanDecimalsScore = (score: string): string => {
    if (score === "N/A") return score;
    const [obtained, total] = score.split("/").map(Number);
    return `${obtained.toFixed(1).replace(".0", "")}/${total.toFixed(1).replace(".0", "")}`;
  };

  const additionalUsers = useMemo(() => connectedUsers.length - 3, [connectedUsers.length]);

  if (!loading.infos && Object.keys(evaluationInfos).length === 0) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.EVAL.ERROR.NOT_FOUND")}</h2>
        <DstButton
          value={translate(language, "PAGES.EVAL.BUTTONS.ALL_EVALUATION_ATTEMPTS")}
          btnClass="btn btn-primary"
          link="/evaluations/attempts"
        />
      </div>
    );
  }

  const comment =
    evaluationInfos.user?.remark?.comment || translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.NO_COMMENTS");

  return (
    <div id="correction-page">
      {loading.infos ? (
        <div className="border-bottom">
          <div className="d-flex align-items-center my-3 mx-2 justify-content-between">
            <Placeholder as="h2" animation="glow" className="w-100">
              <Placeholder xs={8} />
            </Placeholder>
            <div className="d-flex">
              <div className="users-show-eval d-flex me-2">
                {[...Array(3)].map((_, index) => (
                  <Placeholder key={index} as="div" animation="glow" className="me-2">
                    <Placeholder.Button className="rounded-circle object-fit-cover clickable size-img-picture" />
                  </Placeholder>
                ))}
              </div>
              <div className="users-show-eval d-flex me-2">
                {[...Array(2)].map((_, index) => (
                  <Placeholder key={index} as="div" animation="glow" className="me-2">
                    <Placeholder.Button className="rounded-circle object-fit-cover clickable size-img-picture" />
                  </Placeholder>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-bottom">
          <div className="d-flex align-items-center my-3 mx-2 justify-content-between">
            <div className="d-flex">
              <span
                className="dst-icon-arrow-circle-left h2 me-2 mb-0 blue-font clickable"
                onClick={() => navigate("/evaluations/attempts?status=CORRECTION_PENDING,PRE_CORRECTED")}
                role="button"
                aria-label="Go to Evaluations page"
              />
              <h2 className="mb-0">{evaluationInfos.evaluation.name}</h2>
            </div>
            <div className="d-flex" id="action-buttons">
              <div className="d-flex users-show-eval">
                {connectedUsers?.slice(0, 3).map((user) => (
                  <OverlayTrigger
                    key={user.id}
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${user.id}`}>{user.name}</Tooltip>}
                  >
                    <img
                      src={user.image_url ? user.image_url : "/assets/images/user.jpg"}
                      alt={`${user.name}'s profile`}
                      className="rounded-circle object-fit-cover me-2 clickable size-action-button border"
                      onClick={() => navigate(`/user/${user.id}`)}
                    />
                  </OverlayTrigger>
                ))}
                {additionalUsers > 0 && (
                  <Dropdown align="end" id="additional-users">
                    <Dropdown.Toggle
                      as="div"
                      className="rounded-circle white-background d-flex align-items-center justify-content-center me-3 clickable size-action-button"
                    >
                      <span className="s-regular">+{additionalUsers}</span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {connectedUsers.slice(3).map((user: any) => (
                        <Dropdown.Item
                          key={user.id}
                          onClick={() => navigate(`/user/${user.id}`)}
                          className="d-flex align-items-center xs-regular"
                        >
                          <img
                            src={user.image_url ? user.image_url : "/assets/images/user.jpg"}
                            alt={`${user?.name}'s profile`}
                            className="rounded-circle object-fit-cover me-2 size-action-button border"
                          />
                          <span>{user.name}</span>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
              <DstButton
                btnClass="light-gray-background border-0 me-2 p-2 m-regular action-button size-action-button"
                btnImageBefore={<span className="dst-icon-copy-01 d-flex justify-content-center" />}
                value=""
                variant="secondary"
                clickFunction={() => _copyEvalToClipboard()}
              />
              {evaluationInfos.evaluation?.type === "notebook" && (
                <DstButton
                  btnClass="rounded-circle light-gray-background border-0 me-2 p-2 m-regular action-button size-action-button"
                  btnImageBefore={<span className="dst-icon-download-02 d-flex justify-content-center" />}
                  loading={loading.downloadNotebook}
                  value=""
                  variant="secondary"
                  clickFunction={() =>
                    loading.downloadNotebook
                      ? null
                      : downloadEvaluationAttemptNotebook(language, evaluation_attempts_id, (newValue: boolean) =>
                          setLoading((loading) => ({ ...loading, file: newValue }))
                        )
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      <Row className="mx-0">
        <Col md lg={8} className="medium-gray-background p-2 scrollable" id="content-exam">
          {loading.render ? (
            <Spinner className="d-block blue-font" id="render-spinner" />
          ) : notebookTimeoutError ? (
            <div className="alert alert-danger mt-3">{translate(language, "PAGES.EVAL.ERROR.TIMEOUT_RENDER")}</div>
          ) : (
            (() => {
              switch (evaluationInfos.evaluation?.type) {
                case "html":
                  return (
                    <HtmlEvaluationRender
                      evaluationRender={evaluationRender}
                      evaluationInfos={evaluationInfos}
                      isCorrectionStarted={isCorrectionStarted}
                      handleStatusChange={handleStatusChange}
                      setTotalQuestions={setTotalQuestions}
                      setCorrectQuestions={setCorrectQuestions}
                      correctQuestions={correctQuestions}
                    />
                  );
                case "notebook":
                  return (
                    <iframe
                      title="evaluation content render"
                      sandbox="allow-same-origin allow-popups"
                      srcDoc={evaluationRender}
                      className="white-background p-2 w-100 h-100"
                    />
                  );
                case "upload":
                  return (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                      <strong className="mb-2">{translate(language, "PAGES.EVAL.EXAM_UPLOAD.TITLE")}</strong>
                      <div className="d-flex flex-column gap-2">
                        <DstButton
                          clickFunction={() => downloadEvaluationFileFromRender(evaluationRender)}
                          value={translate(language, "PAGES.EVAL.EXAM_UPLOAD.DOWNLOAD_BUTTON")}
                          btnImageBefore={<span className="dst-icon-file-download-03 pe-2 align-text-bottom" />}
                          btnSize="lg"
                        />
                        <div className="text-center">
                          <DstButton
                            btnClass="rounded-circle light-gray-background border-0 p-2 m-regular action-button size-action-button"
                            btnImageBefore={<span className="dst-icon-copy-03 d-flex justify-content-center" />}
                            value=""
                            variant="secondary"
                            clickFunction={_copyCurlCommand}
                          />
                        </div>
                      </div>
                    </div>
                  );
                case "monitored_upload":
                  return (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                      <strong className="mb-2">{translate(language, "PAGES.EVAL.EXAM_UPLOAD.TITLE")}</strong>
                      <div className="d-flex flex-column gap-2">
                        <div className="text-center">
                          <DstButton
                            value={translate(language, "PAGES.EVAL.EXAM_UPLOAD.VIEW_EXTERNAL_RECORD_BUTTON")}
                            btnImageAfter={<span className="dst-icon-recording-02 ps-2 align-text-bottom" />}
                            btnSize="lg"
                            btnClass="mb-2 me-2"
                            clickFunction={_openExternalWithEmail}
                          />
                          <DstButton
                            clickFunction={() => downloadEvaluationFileFromRender(evaluationRender)}
                            value={translate(language, "PAGES.EVAL.EXAM_UPLOAD.DOWNLOAD_BUTTON")}
                            btnImageBefore={<span className="dst-icon-file-download-03 pe-2 align-text-bottom" />}
                            btnSize="lg"
                            btnClass="mb-2"
                          />
                        </div>
                        <div className="text-center">
                          <DstButton
                            btnClass="rounded-circle light-gray-background border-0 p-2 m-regular action-button size-action-button"
                            btnImageBefore={<span className="dst-icon-copy-03 d-flex justify-content-center" />}
                            value=""
                            variant="secondary"
                            clickFunction={_copyCurlCommand}
                          />
                        </div>
                      </div>
                    </div>
                  );
                default:
                  return <p>Evaluation type {evaluationInfos.evaluation?.type} is not handled for the moment.</p>;
              }
            })()
          )}
        </Col>
        <Col md lg={4} className="s-regular d-flex flex-column px-0 justify-content-between" id="eval-info">
          {loading.infos ? (
            <div className="overflow-auto mb-2 px-4 py-3">
              <div className="mb-3">
                <Placeholder as="h3" animation="glow">
                  <Placeholder xs={4} />
                </Placeholder>
                <div className="mt-2">
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={3} />
                  </Placeholder>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={12} />
                  </Placeholder>
                </div>
                <div className="mt-2">
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={3} />
                  </Placeholder>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={12} />
                  </Placeholder>
                </div>
                <div className="mt-2">
                  <Row className="mx-0">
                    <Col className="ps-0">
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={12} />
                      </Placeholder>
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={12} />
                      </Placeholder>
                    </Col>
                    <Col className="pe-0">
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={12} />
                      </Placeholder>
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={12} />
                      </Placeholder>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="mt-2">
                <Placeholder as="h3" animation="glow">
                  <Placeholder xs={12} />
                </Placeholder>
              </div>
              <Row>
                <Col className="mt-2">
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={3} />
                  </Placeholder>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={5} />
                  </Placeholder>
                </Col>
                <Col className="mt-2">
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={3} />
                  </Placeholder>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={5} />
                  </Placeholder>
                </Col>
              </Row>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={3} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={2} />
                </Placeholder>
              </div>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={4} />
                </Placeholder>
                <div className="d-flex border-bottom pb-2 align-items-center">
                  <div className="d-flex">
                    {[...Array(4)].map((_, index) => (
                      <Placeholder
                        key={index}
                        as="span"
                        animation="glow"
                        className="me-1 rounded"
                        style={{ width: "20px", height: "20px" }}
                      >
                        <Placeholder xs={12} />
                      </Placeholder>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={10} />
                </Placeholder>
              </div>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={10} />
                </Placeholder>
                <Row className="mx-0">
                  <Col className="ps-0">
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                  </Col>
                  <Col className="pe-0">
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                  </Col>
                </Row>
              </div>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={4} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
              </div>
              <div className="mt-2">
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={4} />
                </Placeholder>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
              </div>
            </div>
          ) : (
            <div className="overflow-auto mb-2 px-4 py-3">
              <div className="mb-3">
                <h3> {translate(language, "PAGES.EVAL.USER_INFO.TITLE")}</h3>
                <div className="mt-2">
                  <div className="dark-gray-font">{translate(language, "PAGES.EVAL.USER_INFO.LEARNER_NAME")}</div>
                  <div className="d-flex justify-content-between border-bottom pb-2">
                    <div className="d-flex align-items-center">
                      <span className="dst-icon-user-01 me-2" />
                      {evaluationInfos.user.name}
                      <span className="smoke-gray-font ms-2">#{evaluationInfos.user.id}</span>
                      {evaluationInfos.user.flags.includes("VIP") && (
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-vip">VIP</Tooltip>}>
                          <span className="blue-font dst-icon-star-01 pulse ms-2" />
                        </OverlayTrigger>
                      )}
                      <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/users/*" }]}>
                        <a
                          href={`/user/${evaluationInfos.user.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2 text-decoration-none d-flex"
                          title={translate(language, "PAGES.EVAL.EXAM_INFO.OPEN_USER")}
                        >
                          <span className="dst-icon-link-external-01" />
                        </a>
                      </PermissionComponent>
                    </div>
                    <div>
                      {evaluationInfos.user.flags.includes("Top user") && (
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top-user">Top User</Tooltip>}>
                          <span className="blue-font dst-icon-trophy-01 me-1" />
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="dark-gray-font">{translate(language, "PAGES.EVAL.USER_INFO.COHORTS_NAME")}</div>
                  <Dropdown className="border-bottom pb-2 d-flex align-items-center">
                    <span className="dst-icon-users-01 me-2" />
                    <Dropdown.Toggle
                      variant="secondary"
                      className="s-regular py-0 text-wrap"
                      disabled={evaluationInfos.cohorts.length === 0}
                    >
                      {evaluationInfos.cohorts.some((cohort: any) => cohort.linked)
                        ? (() => {
                            const linkedCohort = evaluationInfos.cohorts.find((cohort: any) => cohort.linked);
                            return (
                              <div>
                                {linkedCohort.cohort_name} #{linkedCohort.id} 
                                {linkedCohort.linked
                                  ? ` (${translate(language, "PAGES.EVAL.USER_INFO.LINKED_COHORT")})`
                                  : ""}
                              </div>
                            );
                          })()
                        : translate(language, "PAGES.EVAL.USER_INFO.NO_COHORT_LINKED")}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {[...evaluationInfos.cohorts]
                        .sort((a, b) => (b.linked ? 1 : 0) - (a.linked ? 1 : 0))
                        .map((cohort, index) => (
                          <Dropdown.Item
                            key={index}
                            className="s-bold text-wrap"
                            onClick={(event) => completeNavigate(event, navigate, `/cohort/${cohort.id}`)}
                          >
                            {cohort.cohort_name}
                            <span
                              className="clickable smoke-gray-font ms-2"
                              onClick={(event) => completeNavigate(event, navigate, `/cohort/${cohort.id}`)}
                            >
                              #{cohort.id}
                            </span>
                            {cohort.linked ? ` (${translate(language, "PAGES.EVAL.USER_INFO.LINKED_COHORT")})` : ""}
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="mt-2">
                  <Row className="mx-0">
                    <Col className="ps-0">
                      <div className="dark-gray-font">
                        {translate(language, "PAGES.EVAL.USER_INFO.PROGRAM_MANAGER")}
                      </div>
                      <div className="d-flex border-bottom pb-2">
                        <div>
                          <span className="dst-icon-user-02 me-2" />
                          {evaluationInfos.cohorts?.find((cohort: any) => cohort.linked)?.program_manager ??
                            (evaluationInfos.cohorts?.some((cohort: any) => cohort.linked)
                              ? "N/A"
                              : evaluationInfos.cohorts?.[0]?.program_manager ?? "N/A")}
                        </div>
                      </div>
                    </Col>
                    <Col className="pe-0">
                      <div className="dark-gray-font">{translate(language, "GLOBAL.COHORT_LEADER")} </div>
                      <div className="d-flex border-bottom pb-2">
                        <div>
                          <span className="dst-icon-user-02 me-2" />
                          {evaluationInfos.cohorts?.find((cohort: any) => cohort.linked)?.cohort_leader ??
                            (evaluationInfos.cohorts?.some((cohort: any) => cohort.linked)
                              ? "N/A"
                              : evaluationInfos.cohorts?.[0]?.cohort_leader ?? "N/A")}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="mt-2 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{translate(language, "PAGES.EVAL.EXAM_INFO.TITLE")}</h3>
                <span className="dst-icon-flag-05 me-1 light-gray-background p-2 rounded-circle" />
              </div>
              <Row>
                <Col className="mt-2">
                  <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_STATUS")}</div>
                  <div className="border-bottom pb-2 d-flex align-items-center">
                    {getExamStatusIcon(evaluationInfos.status)}
                    <span className={`text-uppercase ${getStatusColor(evaluationInfos.status)}`}>
                      {evaluationInfos.status}
                    </span>
                  </div>
                </Col>
                <Col className="mt-2">
                  <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_SCORE")}</div>
                  <div className="d-flex border-bottom pb-2">
                    <div className="d-flex align-items-center">
                      <span className="dst-icon-pencil-line me-2" />
                      {evaluationInfos.score === "None/None" ? (
                        "N/A"
                      ) : (
                        <React.Fragment>
                          {_cleanDecimalsScore(evaluationInfos.score)}
                          {evaluationInfos.score !== "N/A" && !evaluationInfos.score.includes("/20.0") && (
                            <Badge pill bg="secondary" className="mx-2 xs-regular lh-1">
                              {_convertToBasicalScore(evaluationInfos.score)}/20
                            </Badge>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="mt-2">
                <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.AI_PREVISION_SCORE")}</div>
                <div className="d-flex border-bottom pb-2 orange-font">
                  <div className="d-flex gap-3 align-items-center">
                    <div>
                      <span className="dst-icon-atom-02 me-2" />
                      {evaluationInfos.ai_prevision_score}
                    </div>
                    {evaluationInfos.ai_prevision_status !== "N/A" && (
                      <div>
                        <DstButton
                          variant=""
                          value={translate(language, "PAGES.EVAL.EXAM_INFO.SHOW_AI_DETAILS")}
                          btnSize="sm"
                          btnClass="me-2 orange-font"
                          clickFunction={() => setshowAiDetailModal(true)}
                        />
                        {showAiDetailModal && (
                          <Popup
                            show={showAiDetailModal}
                            onClose={() => setshowAiDetailModal(false)}
                            modalTitle={translate(language, "PAGES.EVAL.EXAM_INFO.MODAL_AI.TITLE")}
                            size="lg"
                            scrollable
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <strong>{translate(language, "PAGES.EVAL.EXAM_INFO.MODAL_AI.LABELS.SCORE")}</strong>{" "}
                                <span className="orange-font">{evaluationInfos.ai_prevision_score}</span>
                              </div>
                              <div>
                                <strong>{translate(language, "PAGES.EVAL.EXAM_INFO.MODAL_AI.LABELS.STATUS")}</strong>{" "}
                                <span
                                  className={`text-uppercase ${getStatusColor(evaluationInfos.ai_prevision_status)}`}
                                >
                                  {evaluationInfos.ai_prevision_status}
                                </span>
                              </div>
                            </div>

                            <div className="p-3 white-background border rounded">
                              <strong>{translate(language, "PAGES.EVAL.EXAM_INFO.MODAL_AI.LABELS.COMMENT")}</strong>{" "}
                              <p className="mb-0">{evaluationInfos.ai_prevision_comment}</p>
                            </div>

                            <div className="mt-3">
                              <strong>{translate(language, "PAGES.EVAL.EXAM_INFO.MODAL_AI.DATE")}: </strong>
                              <span className="text-nowrap">
                                {formatDateHumanly(language, evaluationInfos.ai_prevision_date, true)}
                              </span>
                            </div>
                          </Popup>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.NUMBER_ATTEMPTS")}</div>
                <div className="d-flex border-bottom pb-2 align-items-center">
                  <span className="dst-icon-file-check-03 me-2" />

                  {[...Array(3)].map((_, attemptIndex) => {
                    const attempt = attemptIndex + 1;
                    let backgroundColorClass = "white-background";

                    if (attempt === evaluationInfos.number_of_attempts) {
                      switch (attempt) {
                        case 1:
                          backgroundColorClass = "medium-green-background text-white";
                          break;
                        case 2:
                          backgroundColorClass = "orange-background text-white";
                          break;
                        case 3:
                          backgroundColorClass = "red-background text-white";
                          break;
                        default:
                          backgroundColorClass = "white-background";
                      }
                    }

                    return (
                      <span key={attempt} className={`me-1 px-2 rounded ${backgroundColorClass}`}>
                        {attempt}
                      </span>
                    );
                  })}

                  {evaluationInfos.number_of_attempts > 3 && (
                    <span className="px-2 rounded electric-blue-background text-white">
                      {evaluationInfos.number_of_attempts}
                    </span>
                  )}
                </div>
              </div>
              {evaluationInfos.evaluation.type !== "upload" && (
                <div className="mt-2">
                  <div className="dark-gray-font">{translate(language, "GLOBAL.DURATION")}</div>
                  <div className="d-flex border-bottom pb-2">
                    <div>
                      <div className="d-flex align-items-center">
                        <span className="dst-icon-clock-stopwatch me-2" />
                        <span className="me-2">
                          {formatSecondsMinutely(~~evaluationInfos.user_duration)} / {evaluationInfos.total_duration}min
                        </span>
                        <ProgressBar
                          id="duration-eval"
                          className="light-blue-background"
                          now={(evaluationInfos.user_duration / 60 / evaluationInfos.total_duration) * 100}
                          label={
                            evaluationInfos.user_duration / 60 / evaluationInfos.total_duration >= 0.2
                              ? `${(
                                  (evaluationInfos.user_duration / 60 / evaluationInfos.total_duration) *
                                  100
                                ).toFixed(0)}%`
                              : ""
                          }
                          style={{
                            width: "150px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-2">
                <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.TITLE")}</div>
                <div className="d-flex flex-column border-bottom pb-2">
                  <div className="mb-2">
                    {!showFullComment ? truncateString(comment) : comment}
                    {comment.length > 100 && (
                      <button
                        onClick={() => setShowFullComment((prev) => !prev)}
                        className="btn btn-link p-0 ms-1 mb-1 xs-regular"
                      >
                        {showFullComment
                          ? translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.SHOW_LESS")
                          : translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.SHOW_MORE")}
                      </button>
                    )}
                  </div>
                  <Row className="mx-0">
                    <Col className="ps-0">
                      <div className="dark-gray-font">
                        {translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.DIFFICULTY_RATE")}
                      </div>
                      {!!evaluationInfos.user?.remark?.difficulty_rate ? (
                        <StarsRating
                          value={evaluationInfos.user.remark.difficulty_rate / 2}
                          count={5}
                          disabled
                          classNamePrefix="custom-rating"
                        />
                      ) : (
                        <span>-</span>
                      )}
                    </Col>
                    <Col className="pe-0">
                      <div className="dark-gray-font">
                        {translate(language, "PAGES.EVAL.EXAM_INFO.REMARK.SELF_RATE")}
                      </div>
                      {!!evaluationInfos.user?.remark?.self_rate ? (
                        <StarsRating
                          value={evaluationInfos.user.remark.self_rate / 2}
                          count={5}
                          disabled
                          classNamePrefix="custom-rating"
                        />
                      ) : (
                        <span>-</span>
                      )}
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="mt-2">
                <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.TITLE")}</div>
                <div className="d-flex border-bottom py-2">
                  <div>
                    <span className="dst-icon-mail-02 me-2" />
                    {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.SENDING_DATE")}
                    <span className="dark-gray-font ms-3">
                      {evaluationInfos.dt_sent ? formatDateHumanly(language, evaluationInfos.dt_sent, true) : "N/A"}
                    </span>
                  </div>
                </div>
                <div className="d-flex border-bottom py-2">
                  <div>
                    <span className="dst-icon-mail-04 me-2" />
                    {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.OPENING_DATE")}
                    <span className="dark-gray-font ms-3">
                      {evaluationInfos.dt_mail_opened
                        ? formatDateHumanly(language, evaluationInfos.dt_mail_opened, true)
                        : "N/A"}
                    </span>
                  </div>
                </div>
                {evaluationInfos.evaluation.type !== "upload" ? (
                  <React.Fragment>
                    <div className="d-flex border-bottom py-2">
                      <div>
                        <span className="dst-icon-play me-2" />
                        {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.START_DATE")}
                        <span className="dark-gray-font ms-3">
                          {evaluationInfos.dt_start
                            ? formatDateHumanly(language, evaluationInfos.dt_start, true)
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="d-flex border-bottom py-2">
                      <div>
                        <span className="dst-icon-check me-2" />
                        {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.END_DATE")}
                        <span className="dark-gray-font ms-3">
                          {evaluationInfos.dt_ended
                            ? formatDateHumanly(language, evaluationInfos.dt_ended, true)
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                ) : (
                  <div className="d-flex border-bottom py-2">
                    <div>
                      <span className="dst-icon-check me-2" />
                      {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.SUBMISSION_DATE")}
                      <span className="dark-gray-font ms-3">
                        {evaluationInfos.dt_ended ? formatDateHumanly(language, evaluationInfos.dt_ended, true) : "N/A"}
                      </span>
                    </div>
                  </div>
                )}
                {evaluationInfos?.ai_prevision_date !== "N/A" && (
                  <div className="d-flex border-bottom py-2">
                    <div>
                      <span className="dst-icon-atom-02 me-2" />
                      {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.PRE_CORRECTION_DATE")}
                      <span className="dark-gray-font ms-3">
                        {formatDateHumanly(language, evaluationInfos.ai_prevision_date, true)}
                      </span>
                    </div>
                  </div>
                )}
                {evaluationInfos.evaluation.type === "notebook" && (
                  <div className="d-flex border-bottom py-2">
                    <div>
                      <span className="dst-icon-fast-backward me-2" />
                      <a href="https://google.com/">
                        {translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_TIMELINE.BACKUPS")}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-2">
                <div className="dark-gray-font">{translate(language, "PAGES.EVAL.EXAM_INFO.EXAM_NAME")}</div>
                <div className="d-flex border-bottom py-2 align-items-center">
                  <span className="dst-icon-file-02 me-2" />
                  {evaluationInfos.evaluation.filename}
                  {evaluationInfos.evaluation.id && (
                    <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/evaluations/*" }]}>
                      <a
                        href={`/evaluation/${evaluationInfos.evaluation.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2 text-decoration-none d-flex"
                        title={translate(language, "PAGES.EVAL.EXAM_INFO.OPEN_EVAL")}
                      >
                        <span className="dst-icon-link-external-01" />
                      </a>
                    </PermissionComponent>
                  )}
                </div>
              </div>
            </div>
          )}
          {loading.infos ? (
            <div className="d-grid px-4">
              <DstButton mock />
            </div>
          ) : (
            !isCorrectionStarted && (
              <div className="d-grid px-4">
                <DstButton
                  value={
                    ["success", "repass", "failed"].includes(evaluationInfos.status)
                      ? `${translate(language, "PAGES.EVAL.CORRECTED_BY")} ${evaluationInfos.corrector?.name}`
                      : hasAlreadyCorrector
                      ? `${translate(language, "PAGES.EVAL.IN_CORRECTION_BY")} ${correctorName}`
                      : translate(language, "PAGES.EVAL.START_CORRECTION")
                  }
                  btnImageBefore={<span className="dst-icon-play me-2" />}
                  clickFunction={() => startCorrection(evaluation_attempts_id, handleShowOverlay, language)}
                  disabled={
                    hasAlreadyCorrector ||
                    ["success", "repass", "failed", "being_corrected", "pre_correction_in_progress"].includes(
                      evaluationInfos.status
                    )
                  }
                />
              </div>
            )
          )}
          {showOverlay && (
            <div id="overlay-exam" className="white-background p-3 border">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{translate(language, "PAGES.EVAL.REVIEW.TITLE")}</h3>
                <span
                  className={`clickable p-2 off-white-background rounded-circle ${
                    isMinimized ? "dst-icon-chevron-up" : "dst-icon-chevron-down"
                  }`}
                  onClick={() => setIsMinimized(!isMinimized)}
                  aria-label="Minimize"
                />
              </div>
              {!isMinimized && (
                <div className="mt-2">
                  <div id="editor-feedback-exam">
                    {evaluationInfos.ai_comment && !isModifiedComment && (
                      <div className="p-1 rounded light-orange-background mt-2 d-flex align-items-center">
                        <span className="fs-6 orange-font dst-icon-atom-02 me-2" />
                        <span className="fs-6 orange-font">{translate(language, "PAGES.EVAL.REVIEW.AI_REMARK")}</span>
                      </div>
                    )}
                    <ReactQuill
                      value={editorContent}
                      onChange={setEditorContent}
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline", "blockquote"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link" /* "image" */],
                          ["code-block"],
                        ],
                      }}
                      placeholder={translate(language, "PAGES.EVAL.REVIEW.PLACEHOLDER")}
                    />
                  </div>
                  <div className="p-1 rounded light-orange-background mt-2 xs-regular">
                    <span className="dst-icon-lightbulb-02 me-2" />
                    {translate(language, "PAGES.EVAL.REVIEW.REMEMBER_LANGUAGE").replace(
                      "{{LANGUAGE}}",
                      evaluationInfos.user.language
                    )}{" "}
                    <ReactCountryFlag
                      svg
                      countryCode={evaluationInfos.user.language.split("-")[1]}
                      title={evaluationInfos.user.language}
                      className="pb-1 h-auto"
                    />
                  </div>
                  <Form.Group controlId="scoreInput" className="mt-3 d-flex align-items-center">
                    <Form.Label className="me-2 mb-0 dark-gray-font">
                      {translate(language, "PAGES.EVAL.REVIEW.EVALUATION_SCORE").replace(
                        "{{SCORE_BASE}}",
                        String(score_base)
                      )}
                    </Form.Label>
                    <Form.Control
                      className="s-regular"
                      type="number"
                      placeholder={translate(language, "PAGES.EVAL.REVIEW.PLACEHOLDER_SCORE").replace(
                        "{{SCORE_BASE}}",
                        String(score_base)
                      )}
                      value={score !== undefined ? score : ""}
                      onChange={(event) => {
                        const input = event.target.value.replace(",", ".");
                        const enteredScore = parseFloat(input);
                        setScore(
                          input === ""
                            ? undefined
                            : !isNaN(enteredScore) && enteredScore >= 0
                            ? Math.min(enteredScore, score_base)
                            : score
                        );
                      }}
                      onKeyDown={(event) => {
                        if (["e", "E", "+", "-"].includes(event.key)) {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-between mt-2">
                    <ExamStatusDropdown onStatusChange={(status) => setExamStatus(status)} initialStatus={examStatus} />
                    <DstButton
                      btnClass="mt-2"
                      value={translate(language, "PAGES.EVAL.BUTTONS.SEND_CORRECTION")}
                      btnSize="lg"
                      btnImageBefore={<span className="dst-icon-send-03 me-1" />}
                      loading={loading.submit}
                      disabled={
                        loading.submit ||
                        (evaluationInfos.evaluation.type === "html" && !isAllCorrected) ||
                        (evaluationInfos.evaluation.type !== "html" &&
                          (examStatus === "" || examStatus === "—" || isAllCorrected))
                      }
                      clickFunction={() => {
                        setLoading((prev) => ({ ...prev, submit: true }));
                        sendCorrection(
                          language,
                          evaluationInfos.id,
                          editorContent,
                          examStatus,
                          score,
                          score_base,
                          answersCorrection,
                          () => {
                            setLoading((prev) => ({ ...prev, infos: true }));
                            ApiService.get(`evaluations/attempts/${evaluation_attempts_id}/details`)
                              .then((resp) => setEvaluationInfos(resp.data.evaluation_attempt_details))
                              .catch((err) => console.error(err))
                              .finally(() => {
                                setIsCorrectionStarted(false);
                                setShowOverlay(false);
                                setLoading((loading) => ({ ...loading, infos: false }));
                              });
                          },
                          () => setLoading((prev) => ({ ...prev, submit: false }))
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" onClose={() => setShowCopyToast(false)} show={showCopyToast} delay={10000} autohide>
          <Toast.Header className="d-flex justify-content-end" />
          <Toast.Body className="text-white">{translate(language, "PAGES.EVAL.EXAM_UPLOAD.LINK_COPIED")}</Toast.Body>
        </Toast>
        <Toast bg="success" onClose={() => setShowCopyEvalToast(false)} show={showCopyEvalToast} delay={3000} autohide>
          <Toast.Body className="text-white">{translate(language, "PAGES.EVAL.EXAM_INFO.COPIED")}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Correction;
